import { useState } from 'react';
import type { ColumnMapping, ValidationError } from '@/lib/excel';
import { getImportBatchToast } from '@/lib/import-error-utils';
import { toast, useExcel, useExcelProcessor } from '@/hooks';
import { ExcelConfig, type ImportBody } from '../utils';
import { useAuth } from '@/contexts/auth-context';
import { QUERY_KEYS } from '@/constants';
import api from '@/lib/api';

interface UseGiayBaoCoImportParams {
  refreshData: () => Promise<unknown>;
}

export const useGiayBaoCoImport = ({ refreshData }: UseGiayBaoCoImportParams) => {
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorData, setErrorData] = useState<{
    errors: ValidationError[];
    columnMapping: Record<string, ColumnMapping>;
  } | null>(null);
  const { processFile } = useExcelProcessor(ExcelConfig);
  const { entityUnit, entity } = useAuth();
  const { getUUIDByCode } = useExcel();

  const handleImportBodies = async (bodies: ImportBody[]) => {
    const BATCH_SIZE = 120;
    let totalCreated = 0;
    let totalFailed = 0;
    let hasError = false;
    let firstError: unknown;

    setIsImporting(true);

    try {
      for (let i = 0; i < bodies.length; i += BATCH_SIZE) {
        const batch = bodies.slice(i, i + BATCH_SIZE);

        try {
          const { data } = await api.post(`/entities/${entity?.slug}/erp/${QUERY_KEYS.GIAY_BAO_CO}/bulk-create/`, {
            list: batch
          });

          if (data && typeof data === 'object' && 'success' in data) {
            totalCreated += data.created ?? 0;
            totalFailed += data.failed ?? 0;
            if (!data.success) hasError = true;
          } else {
            totalCreated += Array.isArray(data) ? data.length : 0;
          }
        } catch (error) {
          console.error('Bulk create error:', error);
          totalFailed += batch.length;
          hasError = true;
          firstError ??= error;
        }
      }

      const importToast = getImportBatchToast({
        hasError,
        totalCreated,
        totalFailed,
        firstError
      });

      toast({
        description: importToast.description,
        variant: importToast.variant
      });
    } finally {
      await refreshData();
      setIsImporting(false);
    }
  };

  const handleImportFromExcel = async (data: { errors?: string; file: File | null; sheetIndex?: string }) => {
    if (!entityUnit?.uuid) {
      toast({ description: 'Không tìm thấy thông tin đơn vị', variant: 'destructive' });
      return;
    }

    if (!data.file) {
      toast({ description: 'Vui lòng chọn file Excel', variant: 'destructive' });
      return;
    }

    const result = await processFile({
      file: data.file,
      sheetIndex: parseInt(data.sheetIndex || '0', 10),
      maxErrors: parseInt(data.errors || '200', 10),
      getUUIDByCode,
      context: { entityUnitUUID: entityUnit.uuid }
    });

    if (result.success && result.data) {
      await handleImportBodies(result.data);
    } else if (result.errors && result.columnMapping) {
      setErrorData({ errors: result.errors, columnMapping: result.columnMapping });
      setShowErrorDialog(true);
    }
  };

  return {
    importDialog: {
      open: isImportDialogOpen,
      isImporting,
      openDialog: () => setIsImportDialogOpen(true),
      closeDialog: () => setIsImportDialogOpen(false),
      onSubmit: handleImportFromExcel
    },
    importErrorDialog: {
      open: showErrorDialog,
      errorData,
      onClose: () => {
        setShowErrorDialog(false);
        setErrorData(null);
      }
    }
  };
};
